import { Button, Progress, Select, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import LogoBlue from './@resources/img/LogoBlue.svg';
import { Check } from './components/Check';
import { Page } from './components/Page';
import { PageCard } from './components/PageCard';
import { PageCardLogo } from './components/PageCardLogo';
import { SplashPane } from './components/SplashPane';
import { useIdentify } from './hooks/useIdentify';
import { Colors } from './patterns/Colors';

const HEARD_FROM = [
    { value: 'friend', label: 'A friend or colleague' },
    { value: 'search', label: 'Search engine' },
    { value: 'llm', label: 'LLM (i.e. ChatGPT)' },
    { value: 'metro_retro', label: 'I used Metro Retro' },
    { value: 'social', label: 'Social media' },
    { value: 'media', label: 'Youtube / Podcast / Newsletter' },
    { value: 'other', label: 'Other' },
];

// Options that reveal a free-text follow-up field.
const HEARD_FROM_DETAIL: Record<string, string> = {
    llm: 'What did you ask to find us?',
    other: 'Tell us more',
};

const ORG_SIZE = [
    { value: '1', label: 'Just me' },
    { value: '2-10', label: '2 – 10' },
    { value: '11-50', label: '11 – 50' },
    { value: '51-200', label: '51 – 200' },
    { value: '201-1000', label: '201 – 1,000' },
    { value: '1000+', label: '1,000+' },
];

const MESSAGES = [
    'Shredding confetti…',
    'Slicing stickies…',
    'Arranging chairs…',
    'Painting tokens…',
    'Organizing fun…',
    'Hardly working…',
    'Farming pixels…',
    'Cataloging emoji…',
    'Shaping shapes…',
    'Threading connectors…',
    'Washing hats…',
    'Popping toast…',
    'Sharpening arrows…',
    'Painting background…',
    'Winding spinners…',
    'Inserting coin…',
];

interface OperationInfo {
    operationId: string;
    status: string;
    tasks: { current: string, total: number, remaining: number };
    accountId?: string;
}

const startSetup = async (label: string, size: string | null, heardFrom: string | null, heardFromDetail: string): Promise<OperationInfo> => {
    const res = await fetch('/api/v2/internal.setup.accountNext', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label, size, heardFrom, heardFromDetail }),
    });
    if (!res.ok) throw new Error(`Status ${res.status}`);
    return res.json();
};

const fetchOperation = async (operationId: string): Promise<OperationInfo> => {
    const res = await fetch(`/api/v2/operations.info?operationId=${encodeURIComponent(operationId)}`);
    if (!res.ok) throw new Error(`Status ${res.status}`);
    return res.json();
};

export interface CreateAccountValues {
    name: string;
    heardFrom: string | null;
    size: string | null;
    heardFromDetail: string;
}

const LoginCardFrame = styled.div<{ visible: boolean }>`
    transition: all .2s cubic-bezier(0,.76,.47,1);
    opacity: ${props => props.visible ? 1 : 0};
    transform: ${props => `scale(${props.visible ? 1 : 0.95})`};
`;

export const CreateOrganizationPage = () => {

    useIdentify();

    const [submitted, setSubmitted] = useState<CreateAccountValues | null>(null);

    return <Page>
        <LoginCardFrame visible={!submitted}>
            <PageCard splash={<SplashPane />}>
                <PageCardLogo />
                <CreateAccountView onSubmit={setSubmitted} />
            </PageCard>
        </LoginCardFrame>
        {submitted && <CreateAccountRunner values={submitted} onCancel={() => setSubmitted(null)} />}
    </Page>;
};

interface CreateAccountViewProps {
    onSubmit: (values: CreateAccountValues) => void;
}

export const CreateAccountView = ({ onSubmit }: CreateAccountViewProps) => {

    const form = useForm<CreateAccountValues>({
        mode: 'uncontrolled',
        initialValues: {
            name: '',
            heardFrom: null,
            size: null,
            heardFromDetail: '',
        },
        validate: {
            name: value => value.trim() ? null : 'Organization name is required',
            heardFrom: value => value ? null : 'Please pick an option',
            size: value => value ? null : 'Please pick an option',
        },
    });

    // The form is uncontrolled, so track the selected option separately to drive the
    // conditional follow-up field.
    const [heard, setHeard] = useState<string | null>(null);
    const heardFromProps = form.getInputProps('heardFrom');
    const detailLabel = heard ? HEARD_FROM_DETAIL[heard] : undefined;

    return <>
        <Heading>Create a new organization</Heading>
        <Subhead>
            An organization is your shared workspace in Spreo. You'll be able to invite teammates and collaborate on boards together once it's set up.
        </Subhead>
        <Form onSubmit={form.onSubmit(values => onSubmit({ ...values, name: values.name.trim() }))}>
            <TextInput
                label="Organization name"
                placeholder="Acme Inc."
                autoFocus
                w="100%"
                mb="md"
                {...form.getInputProps('name')}
            />
            <Select
                label="How did you hear about us?"
                placeholder="Pick one"
                data={HEARD_FROM}
                w="100%"
                mb="md"
                {...heardFromProps}
                onChange={value => {
                    heardFromProps.onChange?.(value);
                    setHeard(value);
                    if (!value || !HEARD_FROM_DETAIL[value]) {
                        form.setFieldValue('heardFromDetail', '');
                    }
                }}
            />
            {detailLabel && (
                <TextInput
                    label={detailLabel}
                    placeholder="Tell us a little more"
                    w="100%"
                    mb="md"
                    {...form.getInputProps('heardFromDetail')}
                />
            )}
            <Select
                label="How big is your organization?"
                placeholder="Pick a size"
                data={ORG_SIZE}
                w="100%"
                mb="md"
                {...form.getInputProps('size')}
            />
            <Button
                type="submit"
                color="primary"
                size="sm"
                fullWidth
                mb="md"
            >
                Create organization
            </Button>
        </Form>
    </>;
};

interface CreateAccountRunnerProps {
    values: CreateAccountValues;
    onCancel: () => void;
}

const RunnerFrame = styled.div`
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.96);
    animation: fadeInScaleUp 0.5s cubic-bezier(0,.82,.19,1.01) 0.25s forwards;

    @keyframes fadeInScaleUp {
        from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.96);
        }
        to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1.0);
        }
    }
`;

const CreateAccountRunner = ({ values, onCancel }: CreateAccountRunnerProps) => {

    const startMutation = useMutation({
        mutationFn: () => startSetup(values.name, values.size, values.heardFrom, values.heardFromDetail),
        onError: err => {
            console.error('setup failed', err);
            onCancel();
        },
    });

    useEffect(() => {
        startMutation.mutate();
    }, []);

    const operationId = startMutation.data?.operationId;

    const pollQuery = useQuery({
        queryKey: ['setupOperation', operationId],
        queryFn: () => fetchOperation(operationId!),
        enabled: !!operationId,
        refetchInterval: query => query.state.data?.status === 'COMPLETED' ? false : 500,
    });

    const latest = pollQuery.data ?? startMutation.data;
    const done = latest?.status === 'COMPLETED';
    const accountId = startMutation.data?.accountId;
    const total = latest?.tasks.total ?? 0;
    const remaining = latest?.tasks.remaining ?? 0;
    const progress = done ? 1 : (total > 0 ? (total - remaining) / total : 0);

    useEffect(() => {
        if (!done || !accountId) return;
        const redirect = '/boards?popup=welcome';
        const url = `/${accountId}?redirect=${encodeURIComponent(redirect)}`;
        const t = setTimeout(() => { window.location.href = url; }, 1500);
        return () => clearTimeout(t);
    }, [done, accountId]);

    const message = useMemo(
        () => MESSAGES[Math.floor(Math.random() * MESSAGES.length)],
        [latest?.tasks.remaining],
    );

    return <RunnerFrame>
        <Logo src={LogoBlue} />
        <Stage>
            <LoadingRow $visible={!done}>
                <Progress value={Math.round(progress * 100)} color="primary" w="100%" />
                <Message>{message}</Message>
            </LoadingRow>
            <DoneRow $visible={done}>
                <Check value={done} />
                <DoneText>You're all set</DoneText>
            </DoneRow>
        </Stage>
    </RunnerFrame>;
};

const Heading = styled.h1`
    font-size: 1.25rem;
    font-weight: 500;
    color: ${Colors.text};
    margin: 0 0 0.5rem;
`;

const Subhead = styled.p`
    font-size: 0.875rem;
    color: ${Colors.text};
    opacity: 0.7;
    margin: 0 0 1rem;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const Logo = styled.img`
    width: 9rem;
    display: block;
    margin: 0 auto 1.75rem;
`;

const Stage = styled.div`
    display: grid;
    grid-template-areas: 'stack';

    & > * {
        grid-area: stack;
    }
`;

const LoadingRow = styled.div<{ $visible: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    width: 18rem;
    opacity: ${p => p.$visible ? 1 : 0};
    pointer-events: ${p => p.$visible ? 'auto' : 'none'};
    transition: opacity 300ms ease;
`;

const Message = styled.p`
    font-size: 1rem;
    font-weight: 450;
    color: ${Colors.text};
    margin: 0;
    text-align: center;
`;

const DoneRow = styled.div<{ $visible: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    width: 18rem;
    opacity: ${p => p.$visible ? 1 : 0};
    pointer-events: ${p => p.$visible ? 'auto' : 'none'};
    transition: opacity 300ms ease;
`;

const DoneText = styled.p`
    font-size: 1.375rem;
    font-weight: 450;
    color: ${Colors.text};
    margin: 0;
`;
